import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "@/components/Brand";
import { DncBypassBanner } from "@/components/DncBypassBanner";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, Rocket, Users2, HeartHandshake, MessagesSquare, UserCog,
  BarChart3, Settings, FileText, Mic, ShieldBan, ScrollText, LogOut, Menu, X, Gauge, Mailbox, PhoneCall, Zap, Plug, ShoppingCart, CalendarClock, MessageSquareText, Wallet, ShieldCheck, Disc3, Landmark,
} from "lucide-react";

const mloNav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/new-run", label: "New Prospecting Run", icon: Rocket },
  { to: "/prospects", label: "Leads", icon: Users2 },
  { to: "/communications", label: "Communications", icon: MessagesSquare },
  { to: "/appointments", label: "Appointments", icon: CalendarClock },
  { to: "/texts", label: "Texts", icon: MessageSquareText },
  { to: "/marketplace", label: "Lead Marketplace", icon: ShoppingCart, market: true },
  { to: "/wallet", label: "Live Transfers", icon: Wallet },
  { to: "/referrals", label: "Referrals", icon: HeartHandshake },
  { to: "/profile", label: "My Profile", icon: UserCog },
];

const suretyNav = [
  { to: "/surety", label: "Lead Runs", icon: Rocket, end: true },
  { to: "/surety/leads", label: "Contractor Leads", icon: Users2 },
  { to: "/surety/texts", label: "Texts", icon: MessageSquareText },
  { to: "/surety/calls", label: "Call Log", icon: PhoneCall },
  { to: "/surety/appointments", label: "Appointments", icon: CalendarClock },
  { to: "/surety/profile", label: "My Agent Profile", icon: UserCog },
];

const pmNav = [
  { to: "/pm", label: "Owner Leads", icon: Users2, end: true },
  { to: "/pm/rentals", label: "Elite Rentals", icon: Rocket },
  { to: "/pm/reverse", label: "Reverse Prospecting", icon: PhoneCall },
  { to: "/pm/market", label: "Lead Marketplace", icon: Wallet },
  { to: "/pm/appointments", label: "Appointments", icon: CalendarClock },
  { to: "/pm/texts", label: "Texts", icon: MessageSquareText },
  { to: "/pm/emails", label: "Email & Mail Log", icon: Mailbox },
  { to: "/pm/call-recordings", label: "AI Call Recordings", icon: Disc3 },
];

const rtNav = [
  { to: "/realtor", label: "FSBO Leads", icon: Users2, end: true },
  { to: "/realtor/expired", label: "Expired & Cancelled", icon: Rocket },
  { to: "/realtor/reverse", label: "Reverse Prospecting", icon: PhoneCall },
  { to: "/realtor/reo", label: "REO", icon: ShieldCheck },
  { to: "/realtor/payments", label: "Payments", icon: Wallet },
  { to: "/realtor/referrals", label: "Referrals", icon: HeartHandshake },
  { to: "/pm/appointments", label: "Appointments", icon: CalendarClock },
  { to: "/pm/texts", label: "Texts", icon: MessageSquareText },
  { to: "/pm/emails", label: "Email & Mail Log", icon: Mailbox },
  { to: "/pm/call-recordings", label: "AI Call Recordings", icon: Disc3 },
];

const adminNav = [
  { to: "/admin", label: "Overview", icon: BarChart3, end: true },
  { to: "/admin/users", label: "MLO Accounts", icon: Users2 },
  { to: "/admin/templates", label: "Email Templates", icon: FileText },
  { to: "/admin/calls", label: "AI Call Log", icon: PhoneCall },
  { to: "/admin/call-recordings", label: "AI Call Recordings", icon: Disc3 },
  { to: "/admin/live-transfers", label: "Live Transfer Calls", icon: PhoneCall },
  { to: "/admin/surety", label: "Surety Bonds", icon: ShieldCheck },
  { to: "/admin/property-management", label: "Property Mgmt & Realtors", icon: Users2 },
  { to: "/admin/realtors", label: "Realtors", icon: Landmark },
  { to: "/admin/pm-market", label: "Property Management", icon: Users2 },
  { to: "/admin/appointments", label: "Appointments", icon: CalendarClock },
  { to: "/referrals", label: "Referrals", icon: HeartHandshake },
  { to: "/admin/texts", label: "Texts", icon: MessageSquareText },
  { to: "/admin/ai-script", label: "AI Call Script", icon: Mic },
  { to: "/admin/text-script", label: "Texting Script", icon: MessageSquareText },
  { to: "/admin/deity", label: "AI Autopilot Subscriptions", icon: Zap },
  { to: "/admin/astoria", label: "Astoria Leads", icon: Plug },
  { to: "/admin/marketplace", label: "Lead Marketplace", icon: ShoppingCart },
  { to: "/admin/suppression", label: "Suppression / DNC", icon: ShieldBan },
  { to: "/admin/api-usage", label: "API Credits & Spend", icon: Gauge },
  { to: "/admin/stripe", label: "Stripe Payments", icon: Wallet },
  { to: "/admin/postcards", label: "Postcard Designer", icon: Mailbox },
  { to: "/admin/audit", label: "Audit Log", icon: ScrollText },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const items = user?.role === "admin" ? adminNav : user?.role === "surety_agent" ? suretyNav : user?.role === "property_manager" ? pmNav : user?.role === "realtor" ? rtNav : mloNav.filter((it) => !it.market || user?.marketplace_access);

  const NavItems = () => (
    <nav className="flex flex-col gap-1 px-3">
      {items.map((it) => (
        <NavLink
          key={it.to}
          to={it.to}
          end={it.end}
          onClick={() => setOpen(false)}
          data-testid={`nav-${it.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
              isActive
                ? "bg-amber-400/15 text-amber-300"
                : "text-slate-300 hover:text-white hover:bg-white/5"
            }`
          }
        >
          <it.icon className="h-[18px] w-[18px]" />
          {it.label}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sidebar (desktop) */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col navy-panel border-r border-slate-800">
        <div className="px-5 py-6">
          <Brand light />
        </div>
        <div className="flex-1 overflow-y-auto pb-4"><NavItems /></div>
        <div className="border-t border-slate-800 p-4">
          <div className="mb-3 px-1">
            <div className="text-sm font-semibold text-white truncate">
              {user?.first_name} {user?.last_name}
            </div>
            <div className="text-xs text-slate-400 truncate">{user?.email}</div>
            <div className="mt-1 inline-flex items-center rounded-full bg-amber-400/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-300">
              {user?.role === "admin" ? "Administrator" : user?.role === "surety_agent" ? "Surety Bond Agent" : user?.role === "property_manager" ? "Property Manager" : user?.role === "realtor" ? "Realtor" : "Loan Originator"}
            </div>
          </div>
          <Button variant="ghost" data-testid="logout-btn"
            className="w-full justify-start text-slate-300 hover:text-white hover:bg-white/5"
            onClick={async () => { await logout(); nav("/login"); }}>
            <LogOut className="h-4 w-4 mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-40 flex items-center justify-between navy-panel px-4 py-3 border-b border-slate-800">
        <Brand compact light />
        <Button size="icon" variant="ghost" className="text-white" data-testid="mobile-menu-btn"
          onClick={() => setOpen(true)}>
          <Menu className="h-6 w-6" />
        </Button>
      </header>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72 navy-panel flex flex-col animate-fade-up">
            <div className="flex items-center justify-between px-5 py-5">
              <Brand light />
              <Button size="icon" variant="ghost" className="text-white" onClick={() => setOpen(false)}>
                <X className="h-5 w-5" />
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto"><NavItems /></div>
            <div className="border-t border-slate-800 p-4">
              <Button variant="ghost" className="w-full justify-start text-slate-300 hover:text-white"
                onClick={async () => { await logout(); nav("/login"); }}>
                <LogOut className="h-4 w-4 mr-2" /> Sign out
              </Button>
            </div>
          </div>
        </div>
      )}

      <main className="lg:pl-64">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          {user?.role === "admin" && <DncBypassBanner />}
          <Outlet />
        </div>
      </main>
    </div>
  );
}
